import { html, LitElement, nothing, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ProductSelectionContext, productSelectionContextKey } from "../product-selection.context";
import { consume } from "@lit/context";
import { delay, of, switchMap } from "rxjs";
import { AvailableProductItem, TimeSlotItem } from "../../types";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { createRef, Ref, ref } from "lit/directives/ref.js";
import { computePosition, offset } from "@floating-ui/dom";
import { classMap } from "lit/directives/class-map.js";
import autoAnimate, { AnimationController } from "@formkit/auto-animate";
import dayjs from "dayjs";


@customElement("time-slot-picker")
export default class TimeSlotPickerElement extends LitElement {

    private rootElement = createRef<HTMLElement>();
    private animationController: AnimationController | null = null;

    @consume({ context: productSelectionContextKey})
    private productSelectionContext! : ProductSelectionContext;

    @property({ type: Object})
    public product: AvailableProductItem | null = null;

    @state()
    private isLoadingTimeSlots: boolean = false;

    @state()
    private availableTimeSlots: TimeSlotItem[] | null = null;

    @state()
    private selectedTimeSlot: TimeSlotItem | null = null;

    @state()
    private hoveredTimeSlot: TimeSlotItem | null = null;

    private tooltipElement : Ref<HTMLElement> = createRef();


    override async connectedCallback() {
        super.connectedCallback();

        this.availableTimeSlots = this.product?.timeSlotAvailabilities ?? null;

        const delayedTimeSlotLoading = this.productSelectionContext.isLoadingTimeSlots$.pipe(
            switchMap(isLoading =>
                isLoading
                ? of(isLoading) // emit true immediately
                : of(isLoading).pipe(delay(1000)) // delay emitting false
        ));
        delayedTimeSlotLoading.subscribe({
            next: (next) => {
                this.isLoadingTimeSlots = next;
                if(!next) {
                    this.availableTimeSlots = this.product?.timeSlotAvailabilities ?? null;
                }
            }
        });

        this.productSelectionContext.selectedTimeSlot$.subscribe({ next: (next) => this.selectedTimeSlot = next });
    }

    protected override firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        if(this.rootElement.value && !this.animationController) {
            this.animationController = autoAnimate(this.rootElement.value);
        }
    }


    protected override createRenderRoot() {
        return this;
    }

    protected override render() {
        return html`
            <div ${ref(this.rootElement)}>
                <div class="font-bold mb-2">Session Times</div>
                ${when(this.isLoadingTimeSlots,
                    () => this.renderLoadingSkeleton(),
                    () => html`
                        <div class="relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2">
                            ${repeat(this.availableTimeSlots ?? [], timeSlot => timeSlot.timeSlotId, (timeSlot) => {
                                var timeSlotAnchor : Ref<HTMLElement> = createRef();

                                var sessionAvailable = Boolean(
                                    timeSlot.availableCapacity
                                    && timeSlot.availableCapacity > 0
                                    && (!timeSlot.bookingCutOffDate || timeSlot.bookingCutOffDate.isAfter(dayjs().utc().tz(this.productSelectionContext.timeZone)))
                                );

                                var timeSlotClasses = {
                                    "ring-2": this.selectedTimeSlot === timeSlot,
                                    "ring-offset-2": this.selectedTimeSlot === timeSlot,
                                    "cursor-pointer": sessionAvailable,
                                    "ring-mcr-purple": sessionAvailable,
                                    "cursor-not-allowed": !sessionAvailable,
                                };
                                var timeSlotTextClasses = {
                                    // "text-white": sessionAvailable,
                                    "text-shadow-mcr-purple-500": sessionAvailable,
                                    "text-shadow-neutral-400": !sessionAvailable,
                                };
                                return html`
                                    <button ${ref(timeSlotAnchor)}
                                            class="text-white bg-mcr-purple-400 not-disabled:border-mcr-purple-500 disabled:border-neutral-400 disabled:bg-neutral-300 overflow-hidden rounded-lg border-2 hover:not-disabled:ring-mcr-purple-500 hover:not-disabled:ring-2 ${classMap(timeSlotClasses)}"
                                            @mouseenter="${() => {if(sessionAvailable) this.updateHoveredTimeSlot(timeSlotAnchor, timeSlot);}}"
                                            @mouseleave="${() => {this.clearHoveredTimeSlot(timeSlot);}}"
                                            @click="${() => {if(sessionAvailable) this.productSelectionContext.setSelectedTimeSlot(timeSlot);}}"
                                            ?disabled="${!sessionAvailable}">
                                        <div class="relative flex h-8 grow flex-col justify-center">
                                            <!-- <div class="relative flex h-8 grow flex-col justify-center"> -->
                                                <div class="z-10 mx-2 font-semibold ${classMap(timeSlotTextClasses)} text-shadow-sm">
                                                    ${timeSlot.startDate.format('HH:mm')} - ${timeSlot.endDate.format('HH:mm')}
                                                </div>
                                                ${sessionAvailable ? html`
                                                    <div class="bg-mcr-purple-300 absolute h-full w-full"
                                                        style="transform: translateX(${100 - timeSlot.percentAvailable}%);"></div>
                                                ` : nothing}
                                            <!-- </div> -->
                                        </div>
                                    </button>
                                `})}
                            ${this.renderTooltip()}
                        </div>
                    `
                )}
            </div>
        `;
    }



    private renderTooltip() {
        const timeSlot = this.hoveredTimeSlot;
        return html`
            <div class="z-10 absolute w-max top-8 left-0 opacity-0 rounded bg-mcr-opal-800 px-2 py-1 text-white transition pointer-events-none"
                 style="opacity: 0; transition-property: opacity;"
                 ${ref(this.tooltipElement)}>
                <span>${timeSlot?.availableCapacity ?? "unknown"}</span> of <span>${timeSlot?.maxCapacity ?? "unknown"}</span> available
            </div>
        `;
    }
    private clearHoveredTimeSlot(timeSlot: TimeSlotItem) {
        setTimeout(() => {
            // Only clear the hovered time slot if it's the same as the one that triggered
            // the mouse leave, to prevent flickering when moving between buttons
            if (this.hoveredTimeSlot === timeSlot) {
                if(!this.tooltipElement.value) {
                    return;
                }
                Object.assign(this.tooltipElement.value.style, {
                    opacity: "0"
                });
            }
        }, 1000);
    }
    private async updateHoveredTimeSlot(timeSlotAnchor: Ref<HTMLElement>, timeSlot: TimeSlotItem) {
        this.hoveredTimeSlot = timeSlot;

        if(!timeSlotAnchor.value || !this.tooltipElement.value) return;

        const computedPosition = await computePosition(timeSlotAnchor.value, this.tooltipElement.value, {
            placement: 'bottom',
            middleware: [offset(10)],
        });

        Object.assign(this.tooltipElement.value.style, {
            left: `${computedPosition.x}px`,
            top: `${computedPosition.y}px`,
            transitionProperty: this.tooltipElement.value.style.opacity === "1" ? "top, left" : "opacity",
            opacity: "1"
        });
    }


    private renderLoadingSkeleton() {
        return html`
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 animate-pulse gap-2">
                <div class="h-8 w-full bg-gray-300 rounded-lg"></div>
                <div class="h-8 w-full bg-gray-300 rounded-lg"></div>
                <div class="h-8 w-full bg-gray-300 rounded-lg"></div>
            </div>
        `;
    }
}